import { useAuiState } from '@assistant-ui/react';
import { useShallow } from 'zustand/shallow';

import type { BindingResolver, Recipe } from '#core/lib/expr/session/bindingResolver.ts';
import { useEvalDeps } from '#core/lib/expr/session/EvalDepsContext.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { createSessionBindingResolver } from '#core/lib/expr/session/useEvalExpr.ts';

import { recipesFromParts } from '#plugin-chat/lib/transport/bindingProjection.ts';

/**
 * Render-time, position-scoped cross-turn {@link BindingResolver} for a
 * tool-call part. The cross-turn dataset namespace is a **pure
 * projection of the active message branch** — there is no mutable
 * store and nothing to hydrate up front. This hook derives, for the
 * tool-call part currently being rendered, the ordered producer recipes
 * visible *strictly before it* (every producer in prior active-branch
 * messages, plus the earlier tool-call parts of its own message,
 * including ones still streaming), and builds a resolver that
 * lazily re-evaluates them on demand, memoized through `session.fetch`.
 *
 * Mounted by the `<Tool>` render boundary as `<BindingScopeContext>` so
 * the tool's subtree (`<MaterializedExprPreview>`, `<DashboardResult>`,
 * `useResolvedColumns`) resolves `(col <name> …)` against exactly the
 * datasets that existed at that point in the transcript.
 *
 * **Stability:** `useAuiState` has no selector-equality option (only
 * `Object.is` bail-out on the snapshot), and `s.thread.messages` /
 * `s.message.parts` churn a fresh array identity on every streamed
 * token. The selector therefore projects to a **flat primitive array**
 * `[name, expr, name, expr, …]` and is wrapped in `useShallow`: an
 * unchanged producer prefix returns the previous reference, so a
 * streamed token that adds no new successful producer neither
 * re-renders the tool subtree nor re-keys its Suspense queries. A new
 * producer / a producer reaching success / a branch divergence changes
 * the contents → new reference → the downstream `bindings.recipes`
 * query key changes and resolution recomputes. No hand-rolled signature
 * string; TanStack hashes the structured `recipes` key itself.
 */
export function useBindingScope(): BindingResolver {
  const session = useSession();
  const deps = useEvalDeps();

  const flat = useAuiState(
    useShallow((s): readonly string[] => {
      if (s.part.type !== 'tool-call') {
        return [];
      }
      const currentId = s.part.toolCallId;
      const priorRecipes = s.thread.messages.slice(0, s.message.index).flatMap((m) => recipesFromParts(m.parts ?? []));
      const parts = s.message.parts;
      const myIdx = parts.findIndex((p) => p.type === 'tool-call' && p.toolCallId === currentId);
      const currentRecipes = recipesFromParts(myIdx <= 0 ? [] : parts.slice(0, myIdx));
      return [...priorRecipes, ...currentRecipes].flatMap((r) => [r.name, r.expression]);
    }),
  );

  const recipes: Recipe[] = Array.from({ length: flat.length / 2 }, (_, i) => ({
    name: flat[2 * i] as string,
    expression: flat[2 * i + 1] as string,
  }));

  return createSessionBindingResolver(recipes, session, deps);
}
